import { useState, type FormEvent } from 'react'
import { AppShell } from '../components/AppShell'
import { apiErrorMessage, useFundedCycle, usePayouts, useRequestPayout } from '../lib/api'

type Method = 'sepa' | 'wise' | 'usdc'

function fmtEur(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—'
  const n = typeof v === 'string' ? Number(v) : v
  if (!Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n)
}
function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const STATUS_CHIP: Record<string, string> = {
  requested:  'chip--pending', auto_check_passed: 'chip--pending', reviewer_pending: 'chip--pending',
  approved:   'chip--won', paid: 'chip--won',
  rejected:   'chip--lost', cancelled: 'chip--void',
}

export function PayoutPage() {
  const cycle = useFundedCycle()
  const payouts = usePayouts()
  const request = useRequestPayout()
  const [open, setOpen] = useState(false)
  const [method, setMethod] = useState<Method>('sepa')
  const [iban, setIban] = useState('')
  const [name, setName] = useState('')
  const [wallet, setWallet] = useState('')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const dest: Record<string, string> =
      method === 'sepa' ? { iban, accountHolder: name }
      : method === 'wise' ? { wiseEmail: name }
      : { walletAddress: wallet, network: 'erc20' }
    request.mutate(
      { method, destination: dest },
      { onSuccess: () => { setOpen(false); setIban(''); setName(''); setWallet('') } },
    )
  }

  return (
    <AppShell page="billing" breadcrumb={<span className="topbar__breadcrumb-current">Payout</span>}>
      <header className="page-header">
        <div className="page-header__main">
          <p className="page-header__eyebrow"><span>Funded · Payout</span></p>
          <h1 className="page-header__title">Payout</h1>
          <p className="page-header__subtitle">Active cycle and payout history.</p>
        </div>
        <div className="page-header__actions">
          <button type="button" onClick={() => setOpen(true)} className="btn btn--primary">
            <svg className="btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 5 v14 M5 12 h14" />
            </svg>
            Request payout
          </button>
        </div>
      </header>

      <section className="kpi-grid" aria-label="Active cycle"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Balance</span></header>
          <div className="kpi-card__value">{fmtEur(cycle.data?.currentBalance)}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Cycle P&amp;L</span></header>
          <div className="kpi-card__value">{fmtEur(cycle.data?.pnl)}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Expected payout</span></header>
          <div className="kpi-card__value">{fmtEur(cycle.data?.expectedPayout)}</div>
        </article>
        <article className="kpi-card">
          <header className="kpi-card__header"><span className="kpi-card__label">Cycle ends</span></header>
          <div className="kpi-card__value" style={{ fontSize: 'var(--text-md)' }}>
            {cycle.data?.endsAt ? fmtDateTime(cycle.data.endsAt) : '—'}
          </div>
        </article>
      </section>

      <section className="table-card" aria-label="Payout history">
        <header className="table-card__header" style={{ padding: 'var(--space-4)' }}>
          <div className="table-card__title-group">
            <h2 className="table-card__title">Payout history</h2>
            <p className="table-card__subtitle">{payouts.data?.length ?? 0} entries</p>
          </div>
        </header>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Requested</th><th className="is-numeric">Amount</th><th>Destination</th><th>Status</th><th>Paid</th></tr>
            </thead>
            <tbody>
              {(payouts.data ?? []).map((p) => {
                const chipCls = STATUS_CHIP[p.status] ?? 'chip--void'
                return (
                  <tr key={p.id}>
                    <td>{fmtDateTime(p.requestedAt)}</td>
                    <td className="is-numeric numeric">{fmtEur(p.amount)}</td>
                    <td style={{ textTransform: 'capitalize' }}>{p.destination}</td>
                    <td>
                      <span className={`chip ${chipCls}`}>
                        <span className="chip__dot" aria-hidden />{p.status}
                      </span>
                    </td>
                    <td>{p.paidAt ? fmtDateTime(p.paidAt) : '—'}</td>
                  </tr>
                )
              })}
              {(!payouts.data || payouts.data.length === 0) && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: 'var(--space-5)' }}>
                  No payouts yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: 'rgba(0,0,0,0.4)', padding: 'var(--space-4)' }}>
          <form onSubmit={submit} className="settings-card" style={{ width: '100%', maxWidth: 480 }}>
            <header className="settings-card__header">
              <h2 className="settings-card__title">Request payout</h2>
              <p className="settings-card__description">Pick a payout method and confirm the destination.</p>
            </header>
            <div className="settings-card__body">
              <div className="field">
                <label className="field__label" htmlFor="payout-method">Method</label>
                <select id="payout-method" className="field__select"
                  value={method} onChange={(e) => setMethod(e.target.value as Method)}>
                  <option value="sepa">SEPA transfer</option>
                  <option value="wise">Wise</option>
                  <option value="usdc">USDC (ERC-20)</option>
                </select>
              </div>
              {method === 'sepa' && (
                <>
                  <div className="field">
                    <label className="field__label" htmlFor="payout-iban">IBAN</label>
                    <input id="payout-iban" className="field__input" required value={iban} onChange={(e) => setIban(e.target.value)} />
                  </div>
                  <div className="field">
                    <label className="field__label" htmlFor="payout-holder">Account holder</label>
                    <input id="payout-holder" className="field__input" required value={name} onChange={(e) => setName(e.target.value)} />
                  </div>
                </>
              )}
              {method === 'wise' && (
                <div className="field">
                  <label className="field__label" htmlFor="payout-wise-email">Wise email</label>
                  <input id="payout-wise-email" className="field__input" type="email" required value={name} onChange={(e) => setName(e.target.value)} />
                </div>
              )}
              {method === 'usdc' && (
                <div className="field">
                  <label className="field__label" htmlFor="payout-wallet">USDC wallet (ERC-20)</label>
                  <input id="payout-wallet" className="field__input" required value={wallet} onChange={(e) => setWallet(e.target.value)} />
                </div>
              )}
              {request.isError && (
                <div className="auth-card__error" role="alert" style={{ display: 'flex' }}>
                  {apiErrorMessage(request.error)}
                </div>
              )}
            </div>
            <div className="save-bar">
              <div className="save-bar__status" />
              <div className="save-bar__actions" style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button type="button" className="btn btn--ghost" onClick={() => setOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn--primary" disabled={request.isPending}>
                  {request.isPending ? 'Sending…' : 'Request'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  )
}
